if (!customElements.get('nelko-pdp-anchor-tabs')) {
  const REVIEW_FALLBACK_SELECTORS = [
    '#PdpReviews',
    '#judgeme_product_reviews',
    '.jdgm-review-widget',
    '[data-judgeme-product-reviews]',
    '#looxReviews',
    '#shopify-product-reviews',
  ];

  class NelkoPdpAnchorTabs extends HTMLElement {
    connectedCallback() {
      this.sentinel = this.querySelector('[data-tabs-sentinel]');
      this.placeholder = this.querySelector('[data-tabs-placeholder]');
      this.bar = this.querySelector('[data-tabs-bar]');
      this.allTabLinks = [...this.querySelectorAll('[data-anchor-tab]')];
      this.tabLinks = [];
      this.stickyBreakpoint = Number.parseInt(this.dataset.stickyBreakpoint || '750', 10);
      this.enableStickyMobile = this.dataset.enableStickyMobile !== 'false';
      this.scrollLockMs = 800;
      this.isScrollLocked = false;
      this.scrollLockTimer = null;
      this.isFixed = false;
      this.stickyTriggerY = 0;
      this.stickyMediaQuery = window.matchMedia(`(max-width: ${this.stickyBreakpoint}px)`);
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.updateTabVisibility();

      this.bindTabClicks();
      this.setupScrollSpy();

      this.onResize = () => {
        if (this.updateTabVisibility()) this.setupScrollSpy();
        this.measureStickyTrigger();
        this.updateFixedState();
        this.updateTabsHeightVar();
      };

      this.onScroll = () => {
        this.updateFixedState();
      };

      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.stickyMediaQuery.addEventListener('change', this.onResize);

      requestAnimationFrame(() => {
        this.measureStickyTrigger();
        this.updateFixedState();
        this.updateTabsHeightVar();
      });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      this.stickyMediaQuery.removeEventListener('change', this.onResize);
      this.sectionObserver?.disconnect();
      if (this.scrollLockTimer) window.clearTimeout(this.scrollLockTimer);
    }

    resolveTarget(targetId) {
      if (!targetId) return null;

      let target = document.getElementById(targetId);
      if (target) return target;

      if (targetId === 'PdpReviews') {
        for (const selector of REVIEW_FALLBACK_SELECTORS) {
          const node = document.querySelector(selector);
          if (!node) continue;
          return node.closest('.shopify-section') || node;
        }
      }

      return null;
    }

    isUsableTarget(target, targetId) {
      if (!target) return false;

      if (targetId === 'PdpOverview') {
        return [...target.querySelectorAll('img')].some((image) => image.getClientRects().length > 0);
      }

      return true;
    }

    updateTabVisibility() {
      const previousTabs = this.tabLinks;
      for (const tab of this.allTabLinks) {
        const targetId = tab.dataset.targetId || '';
        const target = this.resolveTarget(targetId);
        tab.classList.toggle('is-hidden', !this.isUsableTarget(target, targetId));
        if (tab.classList.contains('is-hidden')) tab.classList.remove('is-active');
      }

      this.tabLinks = this.allTabLinks.filter((tab) => !tab.classList.contains('is-hidden'));
      this.classList.toggle('is-empty', this.tabLinks.length === 0);
      if (!this.tabLinks.some((tab) => tab.classList.contains('is-active'))) {
        this.setActiveTab(this.tabLinks[0]);
      }

      return previousTabs.length !== this.tabLinks.length ||
        previousTabs.some((tab, index) => tab !== this.tabLinks[index]);
    }

    getHeaderBottom() {
      const header = document.querySelector('sticky-header, .section-header');
      if (header) {
        return Math.ceil(header.getBoundingClientRect().bottom);
      }

      const cssHeight = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
      if (cssHeight) {
        const parsed = Number.parseFloat(cssHeight);
        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }

      return 96;
    }

    getScrollOffset() {
      const tabsHeight =
        this.isFixed && this.stickyMediaQuery.matches
          ? this.bar?.getBoundingClientRect().height || 0
          : 0;

      return Math.round(this.getHeaderBottom() + tabsHeight + 8);
    }

    measureStickyTrigger() {
      if (!this.bar || this.isFixed) return;
      this.stickyTriggerY = this.bar.getBoundingClientRect().top + window.scrollY;
    }

    setActiveTab(activeTab) {
      for (const tab of this.tabLinks) {
        tab.classList.toggle('is-active', tab === activeTab);
      }
    }

    bindTabClicks() {
      for (const tab of this.allTabLinks) {
        tab.addEventListener('click', (event) => {
          const targetId = tab.dataset.targetId || '';
          const target = this.resolveTarget(targetId);
          if (!target) return;

          event.preventDefault();
          this.setActiveTab(tab);

          const scrollTop = target.getBoundingClientRect().top + window.scrollY - this.getScrollOffset();
          window.scrollTo({
            top: Math.max(scrollTop, 0),
            behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
          });

          const hashTarget = target.id || targetId;
          if (hashTarget) {
            window.history.replaceState(null, '', `#${hashTarget}`);
          }

          this.lockScrollSpy();
        });
      }
    }

    lockScrollSpy() {
      this.isScrollLocked = true;
      if (this.scrollLockTimer) window.clearTimeout(this.scrollLockTimer);
      this.scrollLockTimer = window.setTimeout(() => {
        this.isScrollLocked = false;
      }, this.scrollLockMs);
    }

    updateTabsHeightVar() {
      if (!this.bar) return;
      const height = this.bar.getBoundingClientRect().height;
      if (height > 0) {
        document.documentElement.style.setProperty('--nelko-pdp-tabs-height', `${height}px`);
      }
    }

    setFixed(shouldFix) {
      if (!this.bar || !this.placeholder) return;

      if (shouldFix === this.isFixed) {
        if (shouldFix) {
          document.documentElement.style.setProperty(
            '--nelko-pdp-tabs-sticky-top',
            `${this.getHeaderBottom()}px`
          );
        }
        return;
      }

      this.isFixed = shouldFix;
      this.bar.classList.toggle('is-fixed', shouldFix);
      this.classList.toggle('is-fixed', shouldFix);

      if (shouldFix) {
        const barHeight = this.bar.offsetHeight;
        this.placeholder.style.height = `${barHeight}px`;
        document.documentElement.style.setProperty(
          '--nelko-pdp-tabs-sticky-top',
          `${this.getHeaderBottom()}px`
        );
      } else {
        this.placeholder.style.height = '0px';
        requestAnimationFrame(() => this.measureStickyTrigger());
      }

      this.updateTabsHeightVar();
    }

    updateFixedState() {
      if (!this.tabLinks.length || !this.bar || !this.placeholder || !this.enableStickyMobile || !this.stickyMediaQuery.matches) {
        this.setFixed(false);
        return;
      }

      if (!this.isFixed) {
        this.measureStickyTrigger();
      }

      const headerBottom = this.getHeaderBottom();
      const shouldFix = window.scrollY + headerBottom >= this.stickyTriggerY;

      this.setFixed(shouldFix);
    }

    setupScrollSpy() {
      this.sectionObserver?.disconnect();
      const sections = this.tabLinks
        .map((tab) => {
          const targetId = tab.dataset.targetId || '';
          const target = this.resolveTarget(targetId);
          if (!target) return null;
          return { tab, target };
        })
        .filter(Boolean);

      if (sections.length === 0) return;

      const offset = this.getScrollOffset();

      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          if (this.isScrollLocked) return;

          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

          if (visible.length === 0) return;

          const match = sections.find(({ target }) => target === visible[0].target);
          if (match) this.setActiveTab(match.tab);
        },
        {
          root: null,
          rootMargin: `-${offset}px 0px -55% 0px`,
          threshold: [0, 0.1, 0.25],
        }
      );

      for (const { target } of sections) {
        this.sectionObserver.observe(target);
      }
    }
  }

  customElements.define('nelko-pdp-anchor-tabs', NelkoPdpAnchorTabs);
}
